/**
 * Editierbare Mietgebühren-Tarife (z.B. Tarif 1, Tarif 3 (Jeki), Schimmel Flügel).
 *
 * Pro Tarif:
 *  - Name, monthly_fee (€), Beschreibung
 *  - Anzahl Instrumente + aktive Verträge
 *  - „Auf aktive Verträge anwenden": Bulk-Update der bestehenden Vertragsgebühren
 *
 * Verwendet als Sub-Tab in /tariffs-discounts.
 */
import React, { useEffect, useState } from 'react';
import { Pencil, Save, X, RefreshCw, Plus, Trash2, Music2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatDeNumber } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const EMPTY = { name: '', monthly_fee: '0', description: '', is_active: true };

export default function RentalTariffsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', monthly_fee: '0', description: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/rental-tariffs');
      setItems(r.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Mietgebühren');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (t) => {
    setEditingId(t.id);
    setDraft({
      name: t.name,
      monthly_fee: String(t.monthly_fee || '0'),
      description: t.description || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); };

  const save = async (id) => {
    try {
      await api.put(`/rental-tariffs/${id}`, {
        name: draft.name,
        monthly_fee: parseFloat(draft.monthly_fee) || 0,
        description: draft.description || null,
      });
      toast.success('Mietgebühr gespeichert');
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const propagate = async (id, name) => {
    if (!window.confirm(`Aktuelle Gebühr auf alle aktiven Verträge mit Tarif "${name}" anwenden?\nDies überschreibt evtl. individuelle Vertrags-Gebühren.`)) return;
    try {
      const r = await api.post(`/rental-tariffs/${id}/propagate`, { apply: true });
      toast.success(`${r.data.updated} von ${r.data.active_rentals} aktiven Verträgen aktualisiert`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Übernahme fehlgeschlagen');
    }
  };

  const create = async () => {
    if (!createDraft.name) { toast.error('Name fehlt'); return; }
    try {
      await api.post('/rental-tariffs', {
        name: createDraft.name,
        monthly_fee: parseFloat(createDraft.monthly_fee) || 0,
        description: createDraft.description || null,
      });
      toast.success('Mietgebühr angelegt');
      setCreateOpen(false);
      setCreateDraft(EMPTY);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const remove = async (id, name, instrumentCount) => {
    if (instrumentCount > 0) {
      toast.error(`Tarif wird noch von ${instrumentCount} Instrumenten verwendet`);
      return;
    }
    if (!window.confirm(`Tarif "${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/rental-tariffs/${id}`);
      toast.success('Tarif gelöscht');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const fmtEur = (v) => `${formatDeNumber(v)} €`;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="rental-tariffs-panel">
      <div className="flex items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Music2 className="w-5 h-5 text-brand-red" /> Mietgebühren · Instrumente
          </h2>
          <p className="text-sm text-slate-500">Pro Mandant editierbare Tarife. Beim Anlegen eines Mietvertrags wird die monatliche Gebühr aus dem zugewiesenen Tarif übernommen.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="rental-tariff-reload">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Neu laden
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="rental-tariff-new">
            <Plus className="w-3.5 h-3.5 mr-1" /> Neu
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 italic">Lade…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 italic">Keine Mietgebühren-Tarife vorhanden.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase border-b border-slate-200">
            <tr>
              <th className="text-left py-2 pr-3">Tarif</th>
              <th className="text-left py-2 pr-3">Beschreibung</th>
              <th className="text-right py-2 pr-3">€/Monat</th>
              <th className="text-right py-2 pr-3">Instrumente</th>
              <th className="text-right py-2 pr-3">Aktive Verträge</th>
              <th className="text-right py-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => {
              const isEditing = editingId === t.id;
              return (
                <tr key={t.id} className="border-b border-slate-100" data-testid={`rental-tariff-row-${t.name}`}>
                  <td className="py-2 pr-3">
                    {isEditing ? (
                      <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-8" />
                    ) : (
                      <span className="font-semibold">{t.name}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {isEditing ? (
                      <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="h-8" placeholder="optional" />
                    ) : (
                      <span className="text-xs italic">{t.description || '—'}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number" step="0.01" min="0"
                        value={draft.monthly_fee}
                        onChange={(e) => setDraft({ ...draft, monthly_fee: e.target.value })}
                        className="h-8 text-right font-mono w-24 ml-auto"
                        data-testid={`rental-tariff-fee-input-${t.name}`}
                      />
                    ) : (
                      <span className={Number(t.monthly_fee) === 0 ? 'text-brand-red font-semibold' : 'text-brand-red font-semibold'}>
                        {fmtEur(t.monthly_fee)}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right text-slate-600 font-mono">{t.instrument_count}</td>
                  <td className="py-2 pr-3 text-right text-slate-600 font-mono">{t.active_rental_count}</td>
                  <td className="py-2 text-right">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={cancelEdit}><X className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" onClick={() => save(t.id)} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid={`rental-tariff-save-${t.name}`}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Speichern
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(t)} title="Bearbeiten" data-testid={`rental-tariff-edit-${t.name}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {t.active_rental_count > 0 && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => propagate(t.id, t.name)}
                            title="Aktuellen Wert auf alle aktiven Verträge anwenden"
                            data-testid={`rental-tariff-propagate-${t.name}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Auf {t.active_rental_count} Verträge anwenden
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => remove(t.id, t.name, t.instrument_count)} title="Löschen" className="text-rose-600 hover:text-rose-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuer Mietgebühren-Tarif</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Name</Label>
              <Input value={createDraft.name} onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })} placeholder="z.B. Tarif 7" data-testid="new-rental-tariff-name" />
            </div>
            <div>
              <Label>Monatliche Gebühr (€)</Label>
              <Input type="number" step="0.01" min="0" value={createDraft.monthly_fee} onChange={(e) => setCreateDraft({ ...createDraft, monthly_fee: e.target.value })} data-testid="new-rental-tariff-fee" />
            </div>
            <div>
              <Label>Beschreibung (optional)</Label>
              <Textarea rows={2} value={createDraft.description} onChange={(e) => setCreateDraft({ ...createDraft, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button onClick={create} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="new-rental-tariff-save">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
